import { useMemo, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useSetAtom } from 'jotai';
import Box from '@mui/material/Box';
import { PageLoader } from '../../shared/PageLoader';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { FieldPanel } from '../TemplateForm/styles';
import { QaqcFieldsPanel } from './QaqcFieldsPanel';
import { QaqcConfigPanel } from './QaqcConfigPanel';
import { QaqcTemplateSettingsPanel } from './QaqcTemplateSettingsPanel';
import { QaqcTemplateActions } from './QaqcTemplateActions';
import { QaqcEquationsPanel } from './QaqcEquationsPanel';
import { workpackTabStyles, FloatingLine } from './styles';
import {
  qaqcTemplateFields,
  selectedQaqcTemplate,
  selectedQaqcTemplateField,
  selectedQaqcTemplateId,
} from '../state';
import {
  LogTemplateType,
  useAllLogsTemplatesByTypeQuery,
  useLogsTemplateQuery,
} from '@/__generated__/graphql';

type QaqcTemplateFormProps = {
  structureId: string;
  currentStructureName: string;
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
};

export const QaqcTemplateForm = ({
  structureId,
  currentStructureName,
  emptyForm,
  handleEmptyForm,
}: QaqcTemplateFormProps) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const setSelectedQaqcTemplateId = useSetAtom(selectedQaqcTemplateId);
  const setSelectedQaqcTemplate = useSetAtom(selectedQaqcTemplate);
  const setQaqcTemplateFields = useSetAtom(qaqcTemplateFields);
  const setSelectedQaqcTemplateField = useSetAtom(selectedQaqcTemplateField);

  const { data: allLogsTemplates, loading: isLogsTemplatesLoading } =
    useAllLogsTemplatesByTypeQuery({
      variables: {
        input: {
          structureId,
          type: LogTemplateType.QaqcTemplate,
        },
      },
      skip: !structureId,
      fetchPolicy: 'cache-first',
    });

  const qaqcTemplate = useMemo(() => {
    return allLogsTemplates?.allLogsTemplates?.at(0) ?? undefined;
  }, [allLogsTemplates?.allLogsTemplates]);

  const hasTemplate = Boolean(qaqcTemplate?.id);

  const { data: qaQcTemplateData, loading: isQaQcTemplateLoading } = useLogsTemplateQuery({
    variables: {
      logsTemplateId: qaqcTemplate?.id ?? '',
    },
    skip: !qaqcTemplate?.id,
  });

  useEffect(() => {
    if (qaqcTemplate?.id) {
      setSelectedQaqcTemplateId(qaqcTemplate.id);
    }
  }, [qaqcTemplate?.id, setSelectedQaqcTemplateId]);

  useEffect(() => {
    if (qaQcTemplateData?.logsTemplate) {
      setSelectedQaqcTemplate(qaQcTemplateData?.logsTemplate);
      setQaqcTemplateFields(qaQcTemplateData?.logsTemplate?.fields);
      setSelectedQaqcTemplateField(qaQcTemplateData?.logsTemplate?.fields?.at(0));
    }
  }, [
    qaqcTemplate,
    qaQcTemplateData?.logsTemplate,
    setSelectedQaqcTemplate,
    setQaqcTemplateFields,
    setSelectedQaqcTemplateField,
  ]);

  useEffect(() => {
    if (hasTemplate && !isQaQcTemplateLoading && emptyForm) {
      handleEmptyForm(false);
    }
  }, [hasTemplate, isQaQcTemplateLoading, emptyForm, handleEmptyForm]);

  const handleTabChange = useCallback(
    (selectedTabIndex: number) => {
      setSelectedTab(selectedTabIndex);
    },
    [setSelectedTab]
  );

  const tabs = useMemo(() => {
    const logsTemplate = qaQcTemplateData?.logsTemplate;
    return [
      {
        index: 0,
        label: 'Settings',
        component: (
          <QaqcTemplateSettingsPanel
            structureId={structureId}
            template={
              logsTemplate
                ? {
                    name: logsTemplate.name,
                    key: logsTemplate.key,
                    supportsMultipleEntries: logsTemplate.supportsMultipleEntries,
                    supportsShifts: logsTemplate.supportsShifts,
                    aggregationLevel: logsTemplate.aggregationLevel,
                  }
                : undefined
            }
          />
        ),
      },
      {
        index: 1,
        label: 'Fields',
        component: (
          <QaqcFieldsPanel
            structureId={structureId}
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
          />
        ),
      },
      {
        index: 2,
        label: 'Equations',
        component: (
          <QaqcEquationsPanel
            structureId={structureId}
            logsTemplate={qaQcTemplateData?.logsTemplate}
          />
        ),
      },
      {
        index: 3,
        label: 'QA/QC Config',
        component: <QaqcConfigPanel structureId={structureId} />,
      },
    ];
  }, [structureId, emptyForm, handleEmptyForm, qaQcTemplateData?.logsTemplate]);

  if (isLogsTemplatesLoading) {
    return <PageLoader />;
  }

  let mainContent: ReactNode;
  if (!hasTemplate) {
    mainContent = (
      <Box sx={{ pt: 2 }}>
        <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
          <EmptyStatePlaceholder type="template" message="No QA/QC Template Exists" />
        </FieldPanel>
      </Box>
    );
  } else if (isQaQcTemplateLoading) {
    mainContent = <PageLoader />;
  } else {
    mainContent = (
      <Box sx={{ pt: 3.5 }}>
        <BasicTabs
          tabs={tabs}
          selectedTab={selectedTab}
          changeTab={handleTabChange}
          tabsStyles={workpackTabStyles}
          customSeperator={<FloatingLine />}
          renderingStrategy="eager"
        />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3 }}>
      <QaqcTemplateActions
        structureId={structureId}
        currentStructureName={currentStructureName}
        hasTemplate={hasTemplate}
        templateForDownload={(qaQcTemplateData?.logsTemplate ?? qaqcTemplate) as unknown}
        templateIdForSync={qaqcTemplate?.id}
        linkedStructureIds={qaQcTemplateData?.logsTemplate?.linkedStructureIds ?? []}
        onAfterAddDefault={() => handleEmptyForm(false)}
      />
      {mainContent}
    </Box>
  );
};
